import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

export const resolveOrganisationFromHostname = (hostname) => {
  if (hostname.includes("mesbonsplans.klesiamut")) {
    return "MES-BONS-PLANS-KLESIA-MUT";
  }
  if (hostname.includes("mesbonsplans.carcept-prev")) {
    return "MES-BONS-PLANS-KLESIA-CARCEPT";
  }
  if (hostname.includes("mesbonsplans.klesia")) {
    return "MES-BONS-PLANS-KLESIA";
  }
  if (hostname.includes("mesbonsplansghr.hapluspme")) {
    return "MES-BONS-PLANS-GHR";
  }
  /* istanbul ignore next */
  return "";
};

export const buildProductStats = (orders) => {
  const productStats = {};
  orders.forEach(order => {
    order.items?.forEach(item => {
      if (!productStats[item.name]) {
        productStats[item.name] = { quantity: 0, total: 0 };
      }
      productStats[item.name].quantity += item.quantity;
      productStats[item.name].total += item.quantity * item.price;
    });
  });
  return productStats;
};

export const getOrderItems = (order) => {
  return Array.isArray(order?.items) ? order.items : null;
};

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [filterEmail, setFilterEmail] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const token = useSelector((state) => state.user.token);
  
  const hostname = window.location.hostname;
  const organisation = resolveOrganisationFromHostname(hostname);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_PREFIXCCPM}api/orders?organisationCode=${organisation}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        const data = await res.json();
        setOrders(data);
        setFilteredOrders(data);
      } catch (err) {
        console.error('Erreur de récupération des commandes :', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [token, organisation]);

  const toggleExpand = (id) => {
    setExpandedOrderId(expandedOrderId === id ? null : id);
  };



  const resetFilters = () => {
    setFilterEmail('');
    setStartDate('');
    setEndDate('');
    setFilteredOrders(orders);
  };

  useEffect(() => {
    const filtered = orders.filter(order => {
      const matchEmail = filterEmail ? order.email?.toLowerCase().includes(filterEmail.toLowerCase()) : true;
      const orderDate = new Date(order.date);
      const matchStartDate = startDate ? orderDate >= new Date(startDate) : true;
      const matchEndDate = endDate ? orderDate <= new Date(endDate) : true;
      return matchEmail && matchStartDate && matchEndDate;
    });
    setFilteredOrders(filtered);
  }, [filterEmail, startDate, endDate, orders]);
  

  const totalOrders = filteredOrders.length;
  const totalRevenue = filteredOrders.reduce((sum, order) => sum + (order.totalPrice || 0), 0);
  const averageOrder = totalOrders > 0 ? (totalRevenue / totalOrders).toFixed(2) : 0;

  const productStats = buildProductStats(filteredOrders);

  const topProducts = Object.entries(productStats)
    .sort(([, a], [, b]) => b.quantity - a.quantity)
    .slice(0, 3);


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-primary mx-auto mb-4"></div>
          <p className="text-theme-primary text-lg">Chargement des commandes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-theme-primary mb-4">Commandes - MBP</h1>
          <p className="text-gray-600 text-lg">Gérez et consultez les commandes synchronisées</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="card-theme">
            <div className="flex items-center">
              <div className="bg-blue-100 text-blue-600 p-3 rounded-lg mr-4">
                <i className="fas fa-shopping-cart text-xl"></i>
              </div>
              <div>
                <p className="text-sm text-gray-600">Total commandes</p>
                <p className="text-3xl font-bold text-theme-primary">{totalOrders}</p>
              </div>
            </div>
          </div>
          
          <div className="card-theme">
            <div className="flex items-center">
              <div className="bg-green-100 text-green-600 p-3 rounded-lg mr-4">
                <i className="fas fa-euro-sign text-xl"></i>
              </div>
              <div>
                <p className="text-sm text-gray-600">Montant total</p>
                <p className="text-3xl font-bold text-theme-primary">{totalRevenue.toFixed(2)} €</p>
              </div>
            </div>
          </div>
          
          <div className="card-theme">
            <div className="flex items-center">
              <div className="bg-purple-100 text-purple-600 p-3 rounded-lg mr-4">
                <i className="fas fa-chart-line text-xl"></i>
              </div>
              <div>
                <p className="text-sm text-gray-600">Montant moyen</p>
                <p className="text-3xl font-bold text-theme-primary">{averageOrder} €</p>
              </div>
            </div>
          </div>
        </div>

        {topProducts.length > 0 && (
          <div className="card-theme mb-8">
            <h3 className="text-2xl font-bold text-theme-primary mb-6 flex items-center">
              <i className="fas fa-trophy text-yellow-500 mr-3"></i>
              Top 3 produits les plus vendus
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {topProducts.map(([name, data], index) => (
                <div key={name} className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center mb-2">
                    <span className="text-2xl mr-2">
                      {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                    </span>
                    <span className="font-semibold text-theme-primary">{name}</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    <p><i className="fas fa-box mr-1"></i> {data.quantity} vendu(s)</p>
                    <p><i className="fas fa-euro-sign mr-1"></i> {data.total.toFixed(2)} €</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}



        <div className="card-theme mb-8">
          <h3 className="text-xl font-bold text-theme-primary mb-4">Filtres</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="fas fa-envelope mr-2 text-theme-accent"></i>
                Filtrer par email
              </label>
              <input
                type="text"
                placeholder="email@example.com"
                value={filterEmail}
                onChange={(e) => setFilterEmail(e.target.value)}
                className="input-theme"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="fas fa-calendar mr-2 text-theme-accent"></i>
                Date de début
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="input-theme"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="fas fa-calendar mr-2 text-theme-accent"></i>
                Date de fin
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="input-theme"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={resetFilters}
                className="btn-theme-secondary w-full"
              >
                <i className="fas fa-undo mr-2"></i>
                Réinitialiser
              </button>
            </div>
          </div>
        </div>

        {filteredOrders.length === 0 ? (
          <div className="card-theme text-center py-12">
            <i className="fas fa-shopping-cart text-6xl text-gray-300 mb-4"></i>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">Aucune commande trouvée</h3>
            <p className="text-gray-500">Ajustez vos filtres pour voir plus de résultats</p>
          </div>
        ) : (
          <div className="card-theme">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Numéro</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Détails</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredOrders.map((order, index) => {
                    const orderKey = order.externalId ?? order.number ?? order.id ?? index;

                    return (
                      <React.Fragment key={orderKey}>
                      <tr className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-theme-primary">{order.number}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(order.date).toLocaleDateString()}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{order.firstname} {order.lastname}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{order.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-theme-primary">{order.totalPrice} €</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            order.status === 'completed' ? 'bg-green-100 text-green-800' : 
                            order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {order.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            className="text-theme-accent hover:text-theme-accent-active transition-colors font-medium"
                            onClick={() => toggleExpand(orderKey)}
                          >
                            <i className={`fas ${expandedOrderId === orderKey ? 'fa-chevron-up' : 'fa-chevron-down'} mr-1`}></i>
                            {expandedOrderId === orderKey ? 'Masquer' : 'Voir'}
                          </button>
                        </td>
                      </tr>
                      {expandedOrderId === orderKey && (
                        <tr>
                          <td colSpan="7" className="px-6 py-4 bg-gray-50">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                              <div>
                                <h4 className="font-semibold text-theme-primary mb-2 flex items-center">
                                  <i className="fas fa-truck mr-2"></i>
                                  Livraison
                                </h4>
                                <ul className="text-sm text-gray-600 space-y-1">
                                  {order.deliveryMethods?.map((d, i) => (
                                    <li key={i} className="flex items-center">
                                      <i className="fas fa-circle text-xs mr-2"></i>
                                      {d.name || d.type}
                                    </li>
                                  )) || <li className="text-gray-400">Aucune</li>}
                                </ul>
                              </div>
                              
                              <div>
                                <h4 className="font-semibold text-theme-primary mb-2 flex items-center">
                                  <i className="fas fa-credit-card mr-2"></i>
                                  Paiement
                                </h4>
                                <ul className="text-sm text-gray-600 space-y-1">
                                  {order.paymentMethods?.map((p, i) => (
                                    <li key={i} className="flex items-center">
                                      <i className="fas fa-circle text-xs mr-2"></i>
                                      {p.name || p.type}
                                    </li>
                                  )) || <li className="text-gray-400">Aucune</li>}
                                </ul>
                              </div>
                              
                              <div>
                                <h4 className="font-semibold text-theme-primary mb-2 flex items-center">
                                  <i className="fas fa-box mr-2"></i>
                                  Articles
                                </h4>
                                <ul className="text-sm text-gray-600 space-y-1">
                                  {(() => {
                                    const items = getOrderItems(order);
                                    return items ? (
                                      items.map((item, i) => (
                                        <li key={i} className="flex justify-between">
                                          <span>{item.name}</span>
                                          <span className="font-medium">{item.quantity} × {item.price} €</span>
                                        </li>
                                      ))
                                    ) : (
                                      <li className="text-gray-400">Pas d'articles</li>
                                    );
                                  })()}
                                </ul>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersPage;